import { useT } from "@/lib/i18n";
import clsx from "clsx";
import { ImageUp, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "./ui";

/** Limite de 5 MB — o mesmo de `MAX_UPLOAD_BYTES` em apps/common/images.py
 * e do `request_body` no deploy/Caddyfile. Se mudar aqui, mude lá.
 *
 * Foto de celular moderno passa fácil de 8 MB, e o dono não vai
 * redimensionar nada antes de subir. Recusar no navegador é bem melhor
 * que esperar o upload inteiro para o Django devolver 413. O que passa
 * ainda é encolhido no servidor antes de virar arquivo em disco. */
const MAX_BYTES = 5 * 1024 * 1024;
const ACEITOS = ["image/jpeg", "image/png", "image/webp"];

export function ImageField({
  label,
  hint,
  value,
  file,
  onPick,
  onRemove,
  shape = "wide",
}: {
  label: string;
  hint?: string;
  /** URL já salva no servidor. */
  value: string | null;
  /** Arquivo escolhido e ainda não enviado. */
  file: File | null;
  onPick: (file: File | null) => void;
  onRemove?: () => void;
  shape?: "wide" | "square" | "circle";
}) {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [previa, setPrevia] = useState<string | null>(null);

  // Prévia local do arquivo escolhido: o dono vê a capa antes de salvar.
  // O revoke no cleanup evita segurar o blob na memória a cada troca.
  useEffect(() => {
    if (!file) return setPrevia(null);
    const url = URL.createObjectURL(file);
    setPrevia(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const escolher = (f: File | null) => {
    setErro(null);
    if (!f) return onPick(null);
    if (!ACEITOS.includes(f.type)) {
      setErro("Use uma imagem JPG, PNG ou WebP.");
      return;
    }
    if (f.size > MAX_BYTES) {
      setErro(t("image.tooBig", { size: (f.size / 1024 / 1024).toFixed(1) }));
      return;
    }
    onPick(f);
  };

  const mostrando = previa ?? value;

  return (
    <div>
      <span className="eyebrow mb-1.5 block text-steel-dim">{label}</span>

      <div className="flex items-start gap-3">
        <div
          className={clsx(
            "flex shrink-0 items-center justify-center overflow-hidden border border-line bg-carbon-900",
            shape === "wide" && "h-20 w-36 rounded-[4px]",
            shape === "square" && "size-20 rounded-[4px]",
            shape === "circle" && "size-20 rounded-full",
          )}
        >
          {mostrando ? (
            <img src={mostrando} alt="" className="size-full object-contain" />
          ) : (
            <ImageUp size={20} className="text-steel-dim" />
          )}
        </div>

        <div className="flex-1 space-y-2">
          <input
            ref={input}
            type="file"
            accept={ACEITOS.join(",")}
            className="hidden"
            onChange={(e) => escolher(e.target.files?.[0] ?? null)}
          />
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="secondary" onClick={() => input.current?.click()}>
              {mostrando ? t("common.edit") : t("image.choose")}
            </Button>
            {value && onRemove && !previa && (
              <Button type="button" size="sm" variant="danger" onClick={onRemove}>
                <Trash2 size={14} /> {t("common.delete")}
              </Button>
            )}
            {previa && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  escolher(null);
                  if (input.current) input.current.value = "";
                }}
              >
                {t("common.undo")}
              </Button>
            )}
          </div>
          {erro ? (
            <p className="text-sm text-red-400">{erro}</p>
          ) : (
            hint && <p className="text-xs text-steel-dim">{hint}</p>
          )}
          {previa && !erro && (
            <p className="text-xs text-racing-400">{t("modal.imageSelected")}</p>
          )}
        </div>
      </div>
    </div>
  );
}
